import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, PieChart, Pie, Legend, CartesianGrid
} from 'recharts';
import {
  Target, CheckCircle2, Clock, AlertTriangle, TrendingUp, Users, UserCheck,
  Calendar, Search, Building2, Bell, X
} from 'lucide-react';

const PRIORITY_COLORS = { High: '#EF4444', Medium: '#F59E0B', Low: '#10B981' };

export default function TasksAnalyticsPage() {
  const { user } = useAuth();
  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const todayStr = today.toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(lastMonth.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(todayStr);
  const [centre, setCentre] = useState('All');
  const [centres, setCentres] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reminding, setReminding] = useState({}); // task_id → bool
  const [bulkReminding, setBulkReminding] = useState(false);

  const allowed = ['CIC', 'AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
  const isAdmin = ['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (isAdmin) {
      api.getCentres().then(({ data }) => setCentres(data || [])).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setLoading(true);
    api.getTaskAnalytics({ fromDate, toDate, centre, search })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fromDate, toDate, centre, search, allowed]);

  const applyPreset = (preset) => {
    const t = new Date();
    const end = t.toISOString().split('T')[0];
    let start;
    if (preset === '7d') {
      const d = new Date(); d.setDate(d.getDate() - 7); start = d.toISOString().split('T')[0];
    } else if (preset === '30d') {
      const d = new Date(); d.setDate(d.getDate() - 30); start = d.toISOString().split('T')[0];
    } else if (preset === '90d') {
      const d = new Date(); d.setDate(d.getDate() - 90); start = d.toISOString().split('T')[0];
    } else if (preset === 'thisMonth') {
      start = new Date(t.getFullYear(), t.getMonth(), 1).toISOString().split('T')[0];
    } else if (preset === 'allTime') {
      start = '2024-01-01';
    }
    setFromDate(start);
    setToDate(end);
  };

  const remind = async (taskId) => {
    setReminding(s => ({ ...s, [taskId]: true }));
    try {
      const res = await api.sendTaskReminder(taskId);
      toast.success(res.data?.overdue ? 'Overdue reminder sent' : 'Reminder sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminding(s => ({ ...s, [taskId]: false }));
    }
  };

  const bulkRemind = async (taskIds) => {
    if (!taskIds.length) return;
    setBulkReminding(true);
    try {
      const res = await api.sendBulkTaskReminders(taskIds);
      toast.success(`Reminders sent: ${res.data?.sent}${res.data?.skipped ? ` (skipped ${res.data.skipped})` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send bulk reminders');
    } finally {
      setBulkReminding(false);
    }
  };

  if (!allowed) {
    return <div data-testid="tasks-analytics-forbidden" className="text-sm text-slate-500">You don&apos;t have access to task analytics.</div>;
  }

  if (loading && !data) return <div className="text-slate-400 text-sm">Loading task analytics...</div>;
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>;
  if (!data) return null;

  const {
    totals, completion_rate, on_time_rate, priority_distribution, completion_trend,
    employees, assigners, centre_breakdown, upcoming_deadlines, overdue_tasks, recent_completions,
  } = data;

  const cards = [
    { key: 'total', label: 'Total Tasks', value: totals.total, icon: Target, color: 'text-slate-800', bg: 'bg-slate-50' },
    { key: 'completed', label: 'Completed', value: totals.completed, icon: CheckCircle2, color: 'text-emerald-700', bg: 'bg-emerald-50' },
    { key: 'pending', label: 'Pending', value: totals.pending, icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50' },
    { key: 'overdue', label: 'Overdue', value: totals.overdue, icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-50' },
  ];

  return (
    <div data-testid="tasks-analytics-page" className="space-y-6 animate-fade-in max-w-[1400px]">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Analytics</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Tasks Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Performance, deadlines, and reminders across all assigned tasks.</p>
        </div>
      </div>

      {/* Filter Bar — period + centre + search */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap" data-testid="task-period-filter">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Period</span>
            </div>
            <input
              data-testid="task-from-date"
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              data-testid="task-to-date"
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500"
            />
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { key: '7d', label: 'Last 7d' },
                { key: '30d', label: 'Last 30d' },
                { key: '90d', label: 'Last 90d' },
                { key: 'thisMonth', label: 'This Month', testid: 'thismonth' },
                { key: 'allTime', label: 'All Time', testid: 'alltime' },
              ].map(p => (
                <button
                  key={p.key}
                  data-testid={`task-preset-${p.testid || p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors"
                >{p.label}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && centres.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="task-centre-filter">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Centre:</span>
                {['All', ...centres.map(c => c.name || c)].map((c) => (
                  <button
                    key={c}
                    data-testid={`task-centre-${(c).toString().toLowerCase()}`}
                    onClick={() => setCentre(c)}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                      centre === c ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >{c === 'All' ? 'All Centres' : c}</button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 ml-auto" data-testid="task-search-wrapper">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                data-testid="task-search-input"
                type="text"
                placeholder="Search by staff or task title..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="text-xs border border-slate-300 rounded-md px-3 py-1.5 min-w-[260px] focus:outline-none focus:border-blue-500"
              />
              {searchInput && (
                <button data-testid="task-search-clear" onClick={() => { setSearchInput(''); setSearch(''); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <Card key={c.key} data-testid={`stat-${c.key}`} className="border border-slate-200 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${c.bg}`}>
                  <Icon className={`w-5 h-5 ${c.color}`} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{c.label}</p>
                  <p className={`text-2xl font-black ${c.color}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{c.value}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Overdue + Upcoming actionable lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="overdue-tasks-card" className="border border-red-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Overdue Tasks ({overdue_tasks.length})
              </CardTitle>
              {overdue_tasks.length > 0 && (
                <button
                  data-testid="remind-all-overdue-btn"
                  disabled={bulkReminding}
                  onClick={() => bulkRemind(overdue_tasks.map(t => t.task_id))}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors inline-flex items-center gap-1"
                >
                  <Bell className="w-3 h-3" /> Remind All
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {overdue_tasks.length === 0 ? (
              <p className="text-sm text-emerald-600 italic text-center py-6">No overdue tasks</p>
            ) : (
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-200 text-[10px] uppercase text-slate-500">
                      <th className="text-left py-2 pr-2">Task</th>
                      <th className="text-left py-2 pr-2">Assignee</th>
                      <th className="text-center py-2 pr-2">Days</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdue_tasks.map(t => (
                      <tr key={t.task_id} data-testid={`overdue-row-${t.task_id}`} className="border-b border-slate-100 hover:bg-red-50/30">
                        <td className="py-2 pr-2">
                          <p className="font-semibold text-slate-800 leading-tight">{t.title}</p>
                          <p className="text-[10px] text-slate-400">Due {t.deadline} · {t.priority}</p>
                        </td>
                        <td className="py-2 pr-2 text-slate-700">
                          <p>{t.assigned_to_name}</p>
                          <p className="text-[10px] text-slate-400">{t.centre}</p>
                        </td>
                        <td className="py-2 pr-2 text-center text-red-700 font-bold">{t.days_overdue}d</td>
                        <td className="py-2 text-right">
                          <button
                            data-testid={`remind-task-${t.task_id}`}
                            disabled={reminding[t.task_id]}
                            onClick={() => remind(t.task_id)}
                            className="text-[11px] font-bold px-2 py-1 rounded bg-red-50 hover:bg-red-100 text-red-700 disabled:opacity-60 inline-flex items-center gap-1"
                          >
                            <Bell className="w-3 h-3" /> Remind
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="upcoming-tasks-card" className="border border-amber-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-700 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Upcoming Deadlines · 7d ({upcoming_deadlines.length})
              </CardTitle>
              {upcoming_deadlines.length > 0 && (
                <button
                  data-testid="remind-all-upcoming-btn"
                  disabled={bulkReminding}
                  onClick={() => bulkRemind(upcoming_deadlines.map(t => t.task_id))}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 transition-colors inline-flex items-center gap-1"
                >
                  <Bell className="w-3 h-3" /> Remind All
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {upcoming_deadlines.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-6">No upcoming tasks in next 7 days</p>
            ) : (
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-200 text-[10px] uppercase text-slate-500">
                      <th className="text-left py-2 pr-2">Task</th>
                      <th className="text-left py-2 pr-2">Assignee</th>
                      <th className="text-center py-2 pr-2">In</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming_deadlines.map(t => (
                      <tr key={t.task_id} data-testid={`upcoming-row-${t.task_id}`} className="border-b border-slate-100 hover:bg-amber-50/30">
                        <td className="py-2 pr-2">
                          <p className="font-semibold text-slate-800 leading-tight">{t.title}</p>
                          <p className="text-[10px] text-slate-400">Due {t.deadline} · {t.priority}</p>
                        </td>
                        <td className="py-2 pr-2 text-slate-700">
                          <p>{t.assigned_to_name}</p>
                          <p className="text-[10px] text-slate-400">{t.centre}</p>
                        </td>
                        <td className="py-2 pr-2 text-center text-amber-700 font-bold">{t.days_left}d</td>
                        <td className="py-2 text-right">
                          <button
                            data-testid={`remind-task-${t.task_id}`}
                            disabled={reminding[t.task_id]}
                            onClick={() => remind(t.task_id)}
                            className="text-[11px] font-bold px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 text-amber-700 disabled:opacity-60 inline-flex items-center gap-1"
                          >
                            <Bell className="w-3 h-3" /> Remind
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rate cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Completion Rate</p>
              <p className="text-4xl font-black text-emerald-700 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{completion_rate}%</p>
              <p className="text-xs text-slate-500 mt-1">{totals.completed} of {totals.total} tasks completed</p>
            </div>
            <CheckCircle2 className="w-12 h-12 text-emerald-100" />
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">On-Time Rate</p>
              <p className="text-4xl font-black text-blue-700 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{on_time_rate}%</p>
              <p className="text-xs text-slate-500 mt-1">{totals.on_time} on-time / {totals.missed_deadline} late completions</p>
            </div>
            <TrendingUp className="w-12 h-12 text-blue-100" />
          </CardContent>
        </Card>
      </div>

      {/* Priority Distribution + Completion Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Priority Mix</CardTitle></CardHeader>
          <CardContent>
            {(priority_distribution || []).every(p => p.value === 0) ? (
              <p className="text-center text-sm text-slate-400 py-12">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={priority_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                    {priority_distribution.map(p => <Cell key={p.name} fill={PRIORITY_COLORS[p.name]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Completion Trend</CardTitle></CardHeader>
          <CardContent>
            {completion_trend.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-12">No tasks completed in this period</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={completion_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Centre breakdown */}
      {centre_breakdown && centre_breakdown.length > 0 && (
        <Card data-testid="centre-breakdown-card" className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-700" /> Centre Breakdown ({centre_breakdown.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-2">Centre</th>
                    <th className="py-2 px-2 text-center">Total</th>
                    <th className="py-2 px-2 text-center text-emerald-600">Completed</th>
                    <th className="py-2 px-2 text-center text-amber-600">Pending</th>
                    <th className="py-2 px-2 text-center text-red-600">Overdue</th>
                    <th className="py-2 px-2">Completion %</th>
                  </tr>
                </thead>
                <tbody>
                  {centre_breakdown.map(c => {
                    const pct = c.total > 0 ? Math.round((c.completed / c.total) * 100) : 0;
                    return (
                      <tr key={c.centre} data-testid={`centre-row-${c.centre.toLowerCase()}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2.5 px-2 font-semibold text-slate-700">{c.centre}</td>
                        <td className="py-2.5 px-2 text-center text-slate-700">{c.total}</td>
                        <td className="py-2.5 px-2 text-center text-emerald-700">{c.completed}</td>
                        <td className="py-2.5 px-2 text-center text-amber-700">{c.pending}</td>
                        <td className="py-2.5 px-2 text-center text-red-700">{c.overdue}</td>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden min-w-[80px]">
                              <div className={`h-full rounded-full transition-all ${pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-bold text-slate-700 w-10 text-right">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Employee Performance Table */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-700" /> Employee Performance ({employees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">No tasks assigned in this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-2">Employee</th>
                    <th className="py-2 px-2">Centre</th>
                    <th className="py-2 px-2 text-center">Total</th>
                    <th className="py-2 px-2 text-center text-emerald-600">Completed</th>
                    <th className="py-2 px-2 text-center text-blue-600">On-time</th>
                    <th className="py-2 px-2 text-center text-amber-600">Pending</th>
                    <th className="py-2 px-2 text-center text-red-600">Overdue</th>
                    <th className="py-2 px-2">Completion %</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(e => {
                    const pct = e.total > 0 ? Math.round((e.completed / e.total) * 100) : 0;
                    return (
                      <tr key={e.user_id} data-testid={`emp-row-${e.user_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2.5 px-2 font-semibold text-slate-700">{e.name}</td>
                        <td className="py-2.5 px-2 text-slate-600 text-xs">{e.centre || 'Unassigned'}</td>
                        <td className="py-2.5 px-2 text-center text-slate-700 font-semibold">{e.total}</td>
                        <td className="py-2.5 px-2 text-center text-emerald-700">{e.completed}</td>
                        <td className="py-2.5 px-2 text-center text-blue-700">{e.on_time}</td>
                        <td className="py-2.5 px-2 text-center text-amber-700">{e.pending}</td>
                        <td className="py-2.5 px-2 text-center text-red-700">{e.overdue}</td>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden min-w-[80px]">
                              <div className={`h-full rounded-full transition-all ${pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-bold text-slate-700 w-10 text-right">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Completions */}
      {recent_completions && recent_completions.length > 0 && (
        <Card data-testid="recent-completions-card" className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Recent Completions ({recent_completions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[260px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2">Task</th>
                    <th className="py-2 pr-2">Assignee</th>
                    <th className="py-2 pr-2">Centre</th>
                    <th className="py-2 pr-2">Completed</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_completions.map(r => (
                    <tr key={r.task_id} className="border-b border-slate-100">
                      <td className="py-2 pr-2 font-medium text-slate-700">{r.title}</td>
                      <td className="py-2 pr-2 text-slate-700">{r.assigned_to_name}</td>
                      <td className="py-2 pr-2 text-slate-500 text-xs">{r.centre}</td>
                      <td className="py-2 pr-2 text-slate-700">{r.completed_date}</td>
                      <td className="py-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.on_time ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {r.on_time ? 'On time' : 'Late'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Assigners */}
      {assigners.length > 0 && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-slate-700" /> Top Assigners ({assigners.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.min(60 + assigners.length * 38, 360)}>
              <BarChart data={assigners.slice(0, 8)} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="tasks_assigned" fill="#3B82F6" name="Assigned" />
                <Bar dataKey="completed_count" fill="#10B981" name="Completed" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
